// Citation enrichment for the References Section: a pure function over the
// answer vocabulary, so Chat, triage and the evidence subagent share one copy.
import type { EnrichedReference, SimpleCitation, Source } from './answers';
import { isFileCitation } from './citationGuards';
import { getDomainFromUrl } from './sourceTypeUtils';

/**
 * Build a URL → page_age lookup from web search sources.
 * Used to enrich citations with page_age from the web search results.
 */
function buildPageAgeLookup(webSearchSources: Source[]): Map<string, string> {
  const lookup = new Map<string, string>();
  for (const source of webSearchSources) {
    if (source.page_age) {
      lookup.set(source.url, source.page_age);
    }
  }
  return lookup;
}

/**
 * Enrich citations with metadata for the References Section.
 *
 * EVI-605: Backend now sends enriched data directly. This function:
 * 1. Ensures domain is always present (uses backend-provided or extracts from URL)
 * 2. Maps backend field names to frontend field names (display_name -> sourceName)
 * 3. Maintains backward compatibility for older citations without enrichment
 *
 * @param citations - Raw citations from text chunks (now include backend enrichment)
 * @param webSearchSources - Sources from web_search_call chunks (have page_age)
 * @returns Enriched references with additional metadata
 */
export function enrichCitations(
  citations: SimpleCitation[],
  webSearchSources: Source[] = []
): EnrichedReference[] {
  // Build URL → page_age lookup from web search sources (still needed for pageAge)
  const pageAgeLookup = buildPageAgeLookup(webSearchSources);

  return citations.map((citation) => {
    // File-based sources (attachments and user_documents) don't have URLs
    const isFileSource = isFileCitation(citation.source_type);

    // Use backend-provided domain, fallback to extracting from URL
    const domain =
      citation.domain || (isFileSource ? '' : getDomainFromUrl(citation.url || ''));

    return {
      ...citation,
      domain,
      // Map backend fields to frontend field names for compatibility
      // Prefer display_name (short) over website_name (full) for sourceName
      sourceName: citation.display_name || citation.website_name,
      websiteType: citation.website_type,
      // Prefer pageAge from citation (sent directly by backend) over lookup
      // File-based sources don't have pageAge
      pageAge:
        citation.pageAge ?? (citation.url ? pageAgeLookup.get(citation.url) : undefined),
    };
  });
}
